import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Check,
  Loader2,
  ShieldCheck,
} from 'lucide-react';

const TOTAL_STEPS = 4;

const DeveloperApiApplicationPage = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    companyType: '',
    contactFirstName: '',
    contactLastName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    useCase: '',
    desiredEndpoints: '',
    integrationType: '',
    expectedVolume: '',
    goLiveTimeline: '',
    writeAccessNeeded: '',
    handlesPhi: '',
    techStack: '',
    hipaaAuditStatus: '',
    auditFirm: '',
    securityNotes: '',
    additionalNotes: '',
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep = (s) => {
    if (s === 1) {
      if (
        !formData.companyName ||
        !formData.website ||
        !formData.contactFirstName ||
        !formData.contactLastName ||
        !formData.contactTitle ||
        !formData.contactEmail
      ) {
        return 'Please fill in your company, website, and primary contact details.';
      }
    }
    if (s === 2) {
      if (!formData.useCase || !formData.desiredEndpoints) {
        return 'Please describe your use case and the endpoints you need.';
      }
    }
    if (s === 4) {
      if (!formData.hipaaAuditStatus) {
        return 'Please tell us the status of your 3rd-party HIPAA audit.';
      }
      if (!acknowledged) {
        return 'Please acknowledge the access requirements to continue.';
      }
    }
    return '';
  };

  const handleNext = () => {
    const err = validateStep(step);
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError('');
    if (step < TOTAL_STEPS) setStep(step + 1);
  };

  const handleBack = () => {
    setSubmitError('');
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateStep(step);
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1');
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        (isLocalhost ? 'http://localhost:8001' : '');

      const payload = {
        ...formData,
        acknowledgedRequirements: acknowledged,
      };

      const response = await fetch(`${apiUrl}/api/developer-api-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Failed to submit application');
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting Developer API application:', error);
      setSubmitError(
        error.message || 'There was an error submitting your application. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Header />

      <style>{`
        /* Reserve the scrollbar gutter so opening dropdowns (which lock body
           scroll) doesn't toggle the scrollbar and jolt the page sideways. */
        html { scrollbar-gutter: stable; }
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>

      <main className="pt-24 pb-12 px-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-white to-indigo-50/50"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-indigo-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-sky-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto max-w-2xl">
          <div className="flex justify-center mb-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-100">
              <ShieldCheck className="w-3.5 h-3.5" />
              Asha Developer API Program
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight text-center mb-3">
            API Developer Application
          </h1>
          <p className="text-center text-slate-500 text-sm mb-6 max-w-lg mx-auto">
            Tell us what you're building and how you handle data. Access is granted through this
            application — including a security review — and pricing is quote-based.
          </p>

          <div className="w-full relative z-10">
            <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden">
              <div className="h-1.5 bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600 transition-all duration-300"
                  style={{ width: `${(isSubmitted ? 1 : step / TOTAL_STEPS) * 100}%` }}
                />
              </div>

              {isSubmitted ? (
                <CardContent className="px-6 py-10">
                  <div className="space-y-5 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-blue-100 bg-blue-50">
                      <CheckCircle2 className="h-7 w-7 text-blue-600" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-slate-900">
                        Application received
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed max-w-md mx-auto">
                        Our team will review your application and follow up at{' '}
                        <span className="font-semibold text-slate-900">{formData.contactEmail}</span>.
                        We'll send the next steps — including our full security controls list and a
                        quote for access — or reach out with a few clarifying questions.
                      </p>
                    </div>
                  </div>
                </CardContent>
              ) : (
                <>
                  <CardHeader className="pb-3 pt-6 px-6">
                    <CardTitle className="text-lg font-bold text-slate-900">
                      {step === 1 && 'Company & contact'}
                      {step === 2 && 'What you’re building'}
                      {step === 3 && 'Access & data'}
                      {step === 4 && 'Security & HIPAA'}
                    </CardTitle>
                    <CardDescription className="text-slate-500 text-sm mt-1">
                      {step === 1 && 'Your organization and who we should work with.'}
                      {step === 2 && 'Your use case, the endpoints you need, and timeline.'}
                      {step === 3 && 'The scope of access and how you handle data.'}
                      {step === 4 && 'A short security questionnaire — required for access.'}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="px-6 pb-6">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (step < TOTAL_STEPS) handleNext();
                        else handleSubmit(e);
                      }}
                    >
                      {/* STEP 1 — Company & contact */}
                      {step === 1 && (
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="companyName" className="text-xs font-semibold text-slate-700">Company name *</Label>
                            <Input
                              id="companyName"
                              placeholder="Acme Health, Inc."
                              value={formData.companyName}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5 col-span-2">
                              <Label htmlFor="website" className="text-xs font-semibold text-slate-700">Website *</Label>
                              <Input
                                id="website"
                                placeholder="https://acmehealth.com"
                                value={formData.website}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="companyType" className="text-xs font-semibold text-slate-700">Type</Label>
                              <Select
                                value={formData.companyType}
                                onValueChange={(value) => handleSelectChange('companyType', value)}
                              >
                                <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="software-vendor">Software vendor</SelectItem>
                                  <SelectItem value="healthtech-platform">Healthtech platform</SelectItem>
                                  <SelectItem value="practice-or-dso">Practice / DSO</SelectItem>
                                  <SelectItem value="systems-integrator">Systems integrator</SelectItem>
                                  <SelectItem value="individual-developer">Individual developer</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="contactFirstName" className="text-xs font-semibold text-slate-700">First name *</Label>
                              <Input
                                id="contactFirstName"
                                placeholder="Jane"
                                value={formData.contactFirstName}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="contactLastName" className="text-xs font-semibold text-slate-700">Last name *</Label>
                              <Input
                                id="contactLastName"
                                placeholder="Doe"
                                value={formData.contactLastName}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="contactTitle" className="text-xs font-semibold text-slate-700">Title / role *</Label>
                            <Input
                              id="contactTitle"
                              placeholder="CTO / Head of Engineering"
                              value={formData.contactTitle}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="contactEmail" className="text-xs font-semibold text-slate-700">Work email *</Label>
                              <Input
                                id="contactEmail"
                                type="email"
                                placeholder="jane@acmehealth.com"
                                value={formData.contactEmail}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="contactPhone" className="text-xs font-semibold text-slate-700">
                                Phone <span className="text-slate-400 font-normal">(optional)</span>
                              </Label>
                              <Input
                                id="contactPhone"
                                type="tel"
                                placeholder="(555) 000-0000"
                                value={formData.contactPhone}
                                onChange={handleInputChange}
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* STEP 2 — Use case */}
                      {step === 2 && (
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="useCase" className="text-xs font-semibold text-slate-700">
                              What are you building on the Asha API? *
                            </Label>
                            <Textarea
                              id="useCase"
                              placeholder="Describe your product and how it would use Asha — e.g. an analytics dashboard, a patient-engagement app, an AI agent that books appointments, etc."
                              value={formData.useCase}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[96px] resize-none transition-all text-sm"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="desiredEndpoints" className="text-xs font-semibold text-slate-700">
                              Which data / endpoints do you need? *
                            </Label>
                            <Textarea
                              id="desiredEndpoints"
                              placeholder="e.g. read patients, appointments, claims; write appointments. Note anything beyond patients & appointments you'd need to write."
                              value={formData.desiredEndpoints}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[80px] resize-none transition-all text-sm"
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="integrationType" className="text-xs font-semibold text-slate-700">Integration type</Label>
                              <Select
                                value={formData.integrationType}
                                onValueChange={(value) => handleSelectChange('integrationType', value)}
                              >
                                <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="read-only">Read-only</SelectItem>
                                  <SelectItem value="read-write">Read + write</SelectItem>
                                  <SelectItem value="agentic-mcp">Agentic / MCP</SelectItem>
                                  <SelectItem value="not-sure">Not sure yet</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="expectedVolume" className="text-xs font-semibold text-slate-700">Expected volume</Label>
                              <Select
                                value={formData.expectedVolume}
                                onValueChange={(value) => handleSelectChange('expectedVolume', value)}
                              >
                                <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="under-10k">&lt; 10k req/mo</SelectItem>
                                  <SelectItem value="10k-100k">10k–100k req/mo</SelectItem>
                                  <SelectItem value="100k-1m">100k–1M req/mo</SelectItem>
                                  <SelectItem value="over-1m">1M+ req/mo</SelectItem>
                                  <SelectItem value="not-sure">Not sure</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="goLiveTimeline" className="text-xs font-semibold text-slate-700">Timeline</Label>
                              <Select
                                value={formData.goLiveTimeline}
                                onValueChange={(value) => handleSelectChange('goLiveTimeline', value)}
                              >
                                <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="asap">ASAP</SelectItem>
                                  <SelectItem value="1-3-months">1–3 months</SelectItem>
                                  <SelectItem value="3-6-months">3–6 months</SelectItem>
                                  <SelectItem value="6-plus-months">6+ months</SelectItem>
                                  <SelectItem value="exploring">Just exploring</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* STEP 3 — Access & data */}
                      {step === 3 && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="writeAccessNeeded" className="text-xs font-semibold text-slate-700">Write access needed</Label>
                              <Select
                                value={formData.writeAccessNeeded}
                                onValueChange={(value) => handleSelectChange('writeAccessNeeded', value)}
                              >
                                <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="read-only">Read-only — no writes</SelectItem>
                                  <SelectItem value="patients-appointments">Patients & appointments</SelectItem>
                                  <SelectItem value="more-than-default">More than patients & appointments</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="handlesPhi" className="text-xs font-semibold text-slate-700">Do you store / process PHI?</Label>
                              <Select
                                value={formData.handlesPhi}
                                onValueChange={(value) => handleSelectChange('handlesPhi', value)}
                              >
                                <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="yes">Yes</SelectItem>
                                  <SelectItem value="no">No</SelectItem>
                                  <SelectItem value="not-sure">Not sure</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="techStack" className="text-xs font-semibold text-slate-700">
                              Your tech stack & hosting
                            </Label>
                            <Textarea
                              id="techStack"
                              placeholder="Languages, frameworks, cloud provider, where data is stored/processed (e.g. Node + Postgres on AWS us-east-1)."
                              value={formData.techStack}
                              onChange={handleInputChange}
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[88px] resize-none transition-all text-sm"
                            />
                          </div>

                          <div className="rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2.5 text-xs text-slate-600 leading-relaxed">
                            <span className="font-semibold text-slate-900">Heads up:</span>{' '}
                            read access spans most fields; write access ships scoped to patients &
                            appointments, with more endpoints available on request. MCP is part of the
                            same API — no separate request needed.
                          </div>
                        </div>
                      )}

                      {/* STEP 4 — Security & HIPAA */}
                      {step === 4 && (
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="hipaaAuditStatus" className="text-xs font-semibold text-slate-700">
                              3rd-party HIPAA audit status *
                            </Label>
                            <Select
                              value={formData.hipaaAuditStatus}
                              onValueChange={(value) => handleSelectChange('hipaaAuditStatus', value)}
                            >
                              <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="current">We have a current 3rd-party HIPAA audit</SelectItem>
                                <SelectItem value="in-progress">In progress</SelectItem>
                                <SelectItem value="not-started">Not yet started</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-[11px] text-slate-500">
                              A 3rd-party audit certifying your stack is HIPAA compliant is required before access is granted.
                            </p>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="auditFirm" className="text-xs font-semibold text-slate-700">
                              Audit firm / compliance tool <span className="text-slate-400 font-normal">(optional)</span>
                            </Label>
                            <Input
                              id="auditFirm"
                              placeholder="Drata, Vanta, A-LIGN, Prescient, etc."
                              value={formData.auditFirm}
                              onChange={handleInputChange}
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="securityNotes" className="text-xs font-semibold text-slate-700">
                              Security posture <span className="text-slate-400 font-normal">(optional)</span>
                            </Label>
                            <Textarea
                              id="securityNotes"
                              placeholder="Briefly: encryption, access controls, SOC 2 status, subprocessors, anything relevant to handling PHI."
                              value={formData.securityNotes}
                              onChange={handleInputChange}
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[72px] resize-none transition-all text-sm"
                            />
                          </div>

                          <button
                            type="button"
                            onClick={() => setAcknowledged((v) => !v)}
                            className="w-full flex items-start gap-3 text-left rounded-md border border-slate-200 bg-slate-50/70 hover:bg-white px-3 py-2.5 transition-all"
                          >
                            <div
                              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                                acknowledged
                                  ? 'bg-blue-600 border-blue-600 text-white'
                                  : 'border-slate-300 bg-white'
                              }`}
                            >
                              {acknowledged && <Check className="h-3 w-3" />}
                            </div>
                            <span className="text-xs text-slate-600 leading-relaxed">
                              I understand that access to the Asha API requires providing a 3rd-party
                              audit certifying our tech stack is HIPAA compliant, and that{' '}
                              <span className="font-semibold text-slate-900">pricing is quote-based</span>. *
                            </span>
                          </button>

                          <div className="space-y-1.5">
                            <Label htmlFor="additionalNotes" className="text-xs font-semibold text-slate-700">
                              Anything else? <span className="text-slate-400 font-normal">(optional)</span>
                            </Label>
                            <Textarea
                              id="additionalNotes"
                              placeholder="Questions, context, or anything that would help us scope access."
                              value={formData.additionalNotes}
                              onChange={handleInputChange}
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[60px] resize-none transition-all text-sm"
                            />
                          </div>
                        </div>
                      )}

                      {submitError && (
                        <p className="text-sm text-red-600 pt-3" role="alert">
                          {submitError}
                        </p>
                      )}

                      {/* Navigation */}
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleBack}
                          disabled={step === 1 || isSubmitting}
                          className="text-slate-600 border-slate-200 hover:bg-slate-50 h-10 px-5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ArrowLeft className="mr-2 w-4 h-4" />
                          Back
                        </Button>

                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-6 text-sm font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                              Submitting...
                            </>
                          ) : step < TOTAL_STEPS ? (
                            <>
                              Continue
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Submit application
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </>
                          )}
                        </Button>
                      </div>

                      <p className="text-[10px] text-center text-slate-400 mt-4">
                        Step {step} of {TOTAL_STEPS}
                      </p>
                    </form>
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DeveloperApiApplicationPage;
